"use client";

import { useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import CircularProgress from "@mui/material/CircularProgress";
import { ToastContainer, toast } from "react-toastify";
import MDBadge from "components/MDBadge";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const BANKS = ["HBL", "UBL", "NBP", "BAL"];
const VOUCHER_TYPES = [
  { label: "Processing", value: "Processing" },
  { label: "Semester Morning", value: "Semester Morning" },
  { label: "Semester Evening", value: "Semester Evening" },
  { label: "Hostel", value: "Hostel" },
];
const VOUCHER_STATUS = ["paid", "un-paid"];
const STUDENT_TYPES_SEMESTER = ["all", "old", "new"];
const STUDENT_TYPES_HOSTEL = ["Old Students", "New Students", "Both"];

function ConsolidatedVoucherReport() {
  const [loading, setLoading] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Filter states
  const [selectedVoucherTypes, setSelectedVoucherTypes] = useState([]);
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterBank, setFilterBank] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [studentType, setStudentType] = useState("all");
  const [fineType, setFineType] = useState("all");
  const [searchText, setSearchText] = useState("");

  // Handle voucher type selection (multi-select)
  const handleVoucherTypeChange = (voucherType) => {
    setSelectedVoucherTypes((prev) => {
      if (prev.includes(voucherType)) {
        return prev.filter((type) => type !== voucherType);
      } else {
        return [...prev, voucherType];
      }
    });
  };

  // Filter vouchers
  const filterVouchers = async () => {
    if (!filterFromDate || !filterToDate || selectedVoucherTypes.length === 0) {
      toast.error("Please select date range and at least one voucher type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        voucher_types: selectedVoucherTypes,
        from_date: filterFromDate,
        to_date: filterToDate,
        bank: filterBank || "",
        status: filterStatus || "",
        student_type: studentType || "all",
        fine_type: fineType || "all",
      };

      const response = await fetch(`${BASE_URL}/fee_voucher/filter_consolidated_vouchers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && Array.isArray(data.vouchers)) {
        setVouchers(data.vouchers);
        toast.success(`Found ${data.vouchers.length} filtered vouchers`);
        setFilterModalOpen(false);
      } else {
        setVouchers([]);
        toast.error(data.error || "No vouchers found for the selected criteria");
      }
    } catch (error) {
      console.error("Error filtering vouchers:", error);
      toast.error("Error filtering vouchers");
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  // Export vouchers function
  const exportVouchers = () => {
    if (!vouchers || vouchers.length === 0) {
      toast.error("No vouchers to export");
      return;
    }
    try {
      const headers = [
        "Voucher Code",
        "Voucher Category",
        "Voucher Type",
        "Status",
        "Amount",
        "Due Date",
        "Transaction ID",
        "Payment Date",
        "Student Name",
        "Student Phone",
        "Student CNIC",
        "Program",
        "Department",
        "Building",
        "Cost Center",
        "BP NO",
        "Payment ID",
        "Bank",
        "Created Date",
      ];

      const csvData = vouchers.map((voucher) => [
        voucher.voucher_code || "",
        voucher.voucher_category || "",
        voucher.voucher_type || "",
        voucher.voucher_status === "paid" || voucher.is_paid === 1 ? "Paid" : "Not Paid",
        voucher.amount || "",
        voucher.due_date || voucher.expiry_date
          ? new Date(voucher.due_date || voucher.expiry_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
              timeZone: "Asia/Karachi",
            })
          : "",
        voucher.transaction_id || "",
        voucher.payment_date || voucher.paid_date
          ? new Date(voucher.payment_date || voucher.paid_date).toLocaleDateString("en-GB")
          : "",
        voucher.student_name || voucher.user_name || voucher.requester_name || "",
        voucher.student_phone || voucher.phone || "",
        voucher.student_cnic || voucher.cnic || "",
        voucher.program_name || "",
        voucher.department_name || "",
        voucher.building || voucher.building_name || "",
        voucher.cost_center || voucher.building_cost_center || "",
        voucher.bp_no || "",
        voucher.payment_id || "",
        voucher.bank || "",
        voucher.created_at || voucher.createdAt
          ? new Date(voucher.created_at || voucher.createdAt).toLocaleDateString("en-GB")
          : "",
      ]);

      const csvContent = [headers, ...csvData]
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `consolidated_voucher_report_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Successfully exported ${vouchers.length} vouchers`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Error exporting vouchers");
    }
  };

  // Reset filter modal
  const resetFilterModal = () => {
    setSelectedVoucherTypes([]);
    setFilterFromDate("");
    setFilterToDate("");
    setFilterBank("");
    setFilterStatus("");
    setStudentType("all");
    setFineType("all");
  };

  // Table columns
  const columns = [
    { Header: "Voucher Code", accessor: "voucher_code", align: "center" },
    { Header: "Category", accessor: "voucher_category", align: "center" },
    { Header: "Voucher Type", accessor: "voucher_type", align: "center" },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: ({ row }) => {
        const status = row.original.voucher_status || (row.original.is_paid === 1 ? "paid" : "un-paid");
        return (
          <MDBadge
            badgeContent={status === "paid" ? "Paid" : "Not Paid"}
            color={status === "paid" ? "success" : "warning"}
            variant="gradient"
            size="sm"
          />
        );
      },
    },
    { Header: "Amount", accessor: "amount", align: "center" },
    {
      Header: "Due Date",
      accessor: "due_date",
      align: "center",
      Cell: ({ value, row }) => {
        const date = value || row.original.expiry_date;
        return date
          ? new Date(date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
              timeZone: "Asia/Karachi",
            })
          : "N/A";
      },
    },
    { Header: "Transaction ID", accessor: "transaction_id", align: "center" },
    {
      Header: "Payment Date",
      accessor: "payment_date",
      align: "center",
      Cell: ({ value, row }) => {
        const date = value || row.original.paid_date;
        return date
          ? new Date(date).toLocaleDateString("en-GB")
          : "N/A";
      },
    },
    {
      Header: "Student Name",
      accessor: "student_name",
      align: "center",
      Cell: ({ value, row }) => {
        return value || row.original.user_name || row.original.requester_name || "N/A";
      },
    },
    {
      Header: "Student CNIC",
      accessor: "student_cnic",
      align: "center",
      Cell: ({ value, row }) => {
        return value || row.original.cnic || "N/A";
      },
    },
    {
      Header: "Program",
      accessor: "program_name",
      align: "center",
    },
    {
      Header: "Department",
      accessor: "department_name",
      align: "center",
    },
    {
      Header: "Building",
      accessor: "building",
      align: "center",
      Cell: ({ value, row }) => {
        return value || row.original.building_name || "N/A";
      },
    },
    {
      Header: "Cost Center",
      accessor: "cost_center",
      align: "center",
      Cell: ({ value, row }) => {
        return value || row.original.building_cost_center || "N/A";
      },
    },
    { Header: "BP No", accessor: "bp_no", align: "center" },
    { Header: "Payment ID", accessor: "payment_id", align: "center" },
    {
      Header: "Bank",
      accessor: "bank",
      align: "center",
      Cell: ({ value, row }) => {
        if (value) return value;
        const transactionId = row.original.transaction_id || "";
        if (transactionId) {
          const bankMatch = BANKS.find((bank) => transactionId.includes(bank));
          return bankMatch || transactionId.split("-")[0] || "N/A";
        }
        return "N/A";
      },
    },
  ];

  // Table rows
  const rows = vouchers.map((v) => ({
    voucher_code: v.voucher_code,
    voucher_category: v.voucher_category || "",
    voucher_type: v.voucher_type || "",
    voucher_status: v.voucher_status || (v.is_paid === 1 ? "paid" : "un-paid"),
    status: v.voucher_status || (v.is_paid === 1 ? "paid" : "un-paid"),
    amount: v.amount || "",
    due_date: v.due_date || v.expiry_date || "",
    transaction_id: v.transaction_id || "N/A",
    payment_date: v.payment_date || v.paid_date || "",
    student_name: v.student_name || v.user_name || v.requester_name || "",
    student_cnic: v.student_cnic || v.cnic || "",
    program_name: v.program_name || "",
    department_name: v.department_name || "",
    building: v.building || v.building_name || "",
    cost_center: v.cost_center || v.building_cost_center || "",
    bp_no: v.bp_no || "",
    payment_id: v.payment_id || "",
    bank: v.bank || "",
    ...v,
  }));

  const searchedRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
      const searchableFields = [
        row.voucher_code,
        row.voucher_category,
        row.voucher_type,
        row.status,
        row.transaction_id,
        row.student_name,
        row.student_cnic,
        row.program_name,
        row.department_name,
        row.building,
        row.cost_center,
        row.bank,
      ];

      return searchableFields.some((value) =>
        (value ?? "").toString().toLowerCase().includes(query)
      );
    });
  }, [rows, searchText]);

  const totalAmount = useMemo(
    () =>
      searchedRows.reduce((sum, voucher) => {
        const amount = Number(voucher.amount || 0);
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0),
    [searchedRows]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className="mb-4">
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Consolidated Voucher Report
                </MDTypography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      color="info"
                      onClick={() => setFilterModalOpen(true)}
                      disabled={loading}
                      style={{ color: "black", width: "100%" }}
                      startIcon={<FilterListIcon />}
                    >
                      Filter Vouchers
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={exportVouchers}
                      disabled={!vouchers || vouchers.length === 0}
                      startIcon={<DownloadIcon />}
                      style={{ color: "white", width: "100%" }}
                    >
                      Export CSV
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card>
              <MDBox pt={3} pb={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : vouchers && vouchers.length > 0 ? (
                  <>
                    <Grid container spacing={2} px={3} pb={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                          <MDTypography variant="button" color="text" fontWeight="regular">
                            Voucher Count
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold">
                            {searchedRows.length}
                          </MDTypography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                          <MDTypography variant="button" color="text" fontWeight="regular">
                            Amount Count
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold">
                            {totalAmount.toLocaleString()}
                          </MDTypography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <InputBase
                            placeholder="Search vouchers..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{ ml: 1, flex: 1 }}
                          />
                        </Paper>
                      </Grid>
                    </Grid>
                    <DataTable
                      table={{ columns, rows: searchedRows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </>
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <MDTypography variant="body1" mt={2}>
                      No vouchers found. Please use filters to see results.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Filter Modal */}
      <Dialog
        open={filterModalOpen}
        onClose={() => {
          setFilterModalOpen(false);
          resetFilterModal();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Filter Consolidated Vouchers</DialogTitle>
        <DialogContent>
          <MDBox mt={2}>
            {/* Multi-select Voucher Types */}
            <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
              Select Voucher Types (Select one or multiple)
            </MDTypography>
            <MDBox mb={2}>
              {VOUCHER_TYPES.map((type) => (
                <FormControlLabel
                  key={type.value}
                  control={
                    <Checkbox
                      checked={selectedVoucherTypes.includes(type.value)}
                      onChange={() => handleVoucherTypeChange(type.value)}
                      color="primary"
                    />
                  }
                  label={type.label}
                />
              ))}
            </MDBox>

            {/* Date Range */}
            <TextField
              fullWidth
              margin="normal"
              label="From Date"
              type="date"
              value={filterFromDate}
              onChange={(e) => setFilterFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="To Date"
              type="date"
              value={filterToDate}
              onChange={(e) => setFilterToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            {/* Status Filter */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Status (Optional)</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status (Optional)"
                style={{ height: "56px" }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {VOUCHER_STATUS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status === "paid" ? "Paid" : "Unpaid"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Student Type Filter */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Student Type</InputLabel>
              <Select
                value={studentType}
                onChange={(e) => setStudentType(e.target.value)}
                label="Student Type"
                style={{ height: "56px" }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="old">Old Student</MenuItem>
                <MenuItem value="new">New Student</MenuItem>
                <MenuItem value="Old Students">Old Students (Hostel)</MenuItem>
                <MenuItem value="New Students">New Students (Hostel)</MenuItem>
                <MenuItem value="Both">Both (Hostel)</MenuItem>
              </Select>
            </FormControl>

            {/* Fine Type Filter (for Semester vouchers) */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Fine Status (Semester Only)</InputLabel>
              <Select
                value={fineType}
                onChange={(e) => setFineType(e.target.value)}
                label="Fine Status (Semester Only)"
                style={{ height: "56px" }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="with_fine">With Fine</MenuItem>
                <MenuItem value="without_fine">Without Fine</MenuItem>
              </Select>
            </FormControl>

            {/* Bank Filter */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Bank (Optional)</InputLabel>
              <Select
                value={filterBank}
                onChange={(e) => setFilterBank(e.target.value)}
                label="Select Bank (Optional)"
                style={{ height: "56px" }}
              >
                <MenuItem value="">
                  <em>All Banks</em>
                </MenuItem>
                {BANKS.map((bank) => (
                  <MenuItem key={bank} value={bank}>
                    {bank}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFilterModalOpen(false);
              resetFilterModal();
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={filterVouchers}
            color="primary"
            variant="contained"
            disabled={
              loading ||
              !filterToDate ||
              !filterFromDate ||
              selectedVoucherTypes.length === 0
            }
            style={{ color: "white" }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Apply Filter"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ConsolidatedVoucherReport;
